import { PaginationRequest } from "src/lib/itaptec/http_client/src";
import {
  GridColumnMenu,
  GridValidRowModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import {
  UseItecDataGridPaginated,
  UseItecDataGridPaginatedProps,
} from "./types";
import { useQuery } from "@tanstack/react-query";
import { useFilter, useCheckboxSelection } from "../common";
import { FilterAlt } from "@mui/icons-material";
import { Filter } from "../Filter";
import { Box, IconButton } from "@mui/material";

const defaultPagination: PaginationRequest = {
  actualPage: 1,
  pageSize: 24,
};

export function dataGridPaginated<
  TEntity extends GridValidRowModel,
  TFilter extends FieldValues = {}
>(
  props: UseItecDataGridPaginatedProps<TEntity, TFilter>
): UseItecDataGridPaginated<TEntity, TFilter> {
  const [paramsPaginacion, setParamsPaginacion] =
    useState<PaginationRequest>(defaultPagination);

  const filter = useFilter<TFilter>({ filters: props.filters });

  const query = useQuery({
    queryKey: ["query", paramsPaginacion, filter?.form.watch()],
    queryFn: () =>
      props
        .getEntitiesPaginated(paramsPaginacion, filter?.form.watch())
        .then((res) => res),
  });

  const { data, refetch, isFetching, isError } = query;

  const apiRef = useGridApiRef();

  const { selectedRows } = useCheckboxSelection<TEntity>(
    data,
    props.getRowId || (() => 0),
    apiRef
  );
  // TODO: habría que mejorar la estructura del código para que cuando no se requiere
  // checkbox selection, no sea necesario el hook.

  return {
    datagridProps: {
      apiRef: apiRef,
      paginationModel: {
        pageSize: data?.paginacion.pageSize || 0,
        page: data?.paginacion.actualPage ? data.paginacion.actualPage - 1 : 0,
      },
      rowCount: query.data?.paginacion.cantRecords,
      columns: props.columns.map((col) => ({
        ...col,
        sortable: false,
        disableColumnMenu: !props.filters?.[col.field],
        renderHeader: (params) => {
          return (
            <Box sx={{ ":hover > *": { visibility: "visible" } }}>
              <strong style={{ marginRight: 10 }}>
                {params.colDef.headerName}
              </strong>
              <>
                {props.filters?.[col.field] && (
                  <IconButton
                    onClick={() => {
                      apiRef.current.showColumnMenu(params.colDef.field);
                      // apiRef.current.showFilterPanel(params.colDef.field);
                    }}
                    sx={{
                      visibility: !!filter?.form.getValues(
                        props.filters[col.field].name
                      )
                        ? "visible"
                        : "hidden",
                    }}
                  >
                    <FilterAlt fontSize="small" />
                  </IconButton>
                )}
              </>
            </Box>
          );
        },
      })),
      rows: data?.lista || [],
      onPaginationModelChange: ({ page, pageSize }) =>
        setParamsPaginacion({ actualPage: page + 1, pageSize: pageSize }),
      loading: isFetching,
      slots: {
        columnMenu: (params) => {
          return (
            <GridColumnMenu
              {...params}
              slots={{
                columnMenuUserItem: (params) => {
                  if (!props.filters || !filter) return <></>;
                  const {
                    colDef: { field },
                  } = params;
                  // TODO: make field of type union of possible fields
                  const menuParams = props.filters[field];
                  
                  if (!menuParams) return <></>;

                  return (
                    <Filter
                      close={() => {
                        apiRef.current.hideColumnMenu();
                      }}
                      formMethods={filter.form}
                      menuParams={menuParams}
                    />
                  );
                },
                columnMenuSortItem: null,
                columnMenuFilterItem: null,
                columnMenuColumnsItem: null,
              }}
            />
          );
        },
      },
      checkboxSelection: props.checkboxSelection,
      rowSelectionModel: props.checkboxSelection
        ? selectedRows.map((row) => props.getRowId(row))
        : undefined,
      getRowId: props.getRowId,
    },
    control: {
      query: query,
      form: props.filters ? filter?.form : undefined,
      selectedRows,
    },
  };
}
